import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Copy, KeyRound, Plus, TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'

import type { ApiKey, ApiKeyScope, CreatedApiKey } from '@/lib/api/api-key-api'
import {
    PartnerPermissionsField,
    orderedPartnerPermissions,
    partnerPermissionLabel,
} from '@/components/api-keys/partner-permissions-field'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useApiKeys,
    useCreateApiKey,
    useRevokeApiKey,
    useUpdateApiKeyPermissions,
} from '@/hooks/use-api-key-query'
import { PARTNER_PERMISSIONS } from '@/lib/api/api-key-api'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/api-keys/')({
    component: ApiKeysPage,
})

function formatExpiry(iso?: string | null): string {
    if (!iso) return m.api_keys_permanent()
    return new Date(iso).toLocaleDateString('vi-VN')
}

type KeyStatus = 'active' | 'expired' | 'revoked'

function keyStatus(k: ApiKey): KeyStatus {
    if (k.revoked_at) return 'revoked'
    if (k.expires_at && new Date(k.expires_at).getTime() <= Date.now()) {
        return 'expired'
    }
    return 'active'
}

// Tên loại khoá theo scope; key cũ chưa có cột scope coi là partner.
function scopeLabel(scope: ApiKeyScope | undefined): string {
    switch (scope) {
        case 'storefront':
            return m.api_keys_scope_storefront()
        case 'shop_auth':
            return m.api_keys_scope_shop_auth()
        default:
            return m.api_keys_scope_partner()
    }
}

// Quyền của khoá trên bảng: chỉ khoá partner có phân quyền; null là khoá cũ
// tạo trước khi có phân quyền nên đang toàn quyền.
function permissionsLabel(k: ApiKey): string {
    if (k.scope && k.scope !== 'partner') return '—'
    if (k.permissions == null) return m.api_keys_permissions_full()
    return k.permissions.map(partnerPermissionLabel).join(', ')
}

// Trần tên khoá — khớp server (API_KEY_NAME_TOO_LONG, max 255).
const API_KEY_NAME_MAX = 255

export function ApiKeysPage() {
    const { hasPermission } = useAuth()
    const canRead = hasPermission('api_keys.read')
    const canWrite = hasPermission('api_keys.write')
    // Only admins may mint web-shop keys; everyone else only ever sees partner.
    const canWriteStorefront = hasPermission('api_keys.write_storefront')

    const { data: keys = [], isLoading } = useApiKeys()
    const createMutation = useCreateApiKey()
    const revokeMutation = useRevokeApiKey()
    const updatePermsMutation = useUpdateApiKeyPermissions()

    const [createOpen, setCreateOpen] = useState(false)
    const [name, setName] = useState('')
    const [expiresAt, setExpiresAt] = useState('')
    const [scope, setScope] = useState<ApiKeyScope>('partner')
    const [nameError, setNameError] = useState('')
    // Khoá partner mới không chọn sẵn quyền nào: người tạo phải chủ động chọn.
    const [permissions, setPermissions] = useState<Set<string>>(new Set())
    const [permissionsError, setPermissionsError] = useState('')
    // Khoá đang sửa quyền (null = đóng hộp thoại sửa).
    const [editing, setEditing] = useState<ApiKey | null>(null)
    const [editPermissions, setEditPermissions] = useState<Set<string>>(
        new Set(),
    )
    const [editError, setEditError] = useState('')
    // Holds the freshly-created key so it can be revealed exactly once.
    const [revealed, setRevealed] = useState<CreatedApiKey | null>(null)

    if (!canRead) {
        return (
            <div className="page-pad">
                <p className="text-sm text-muted-foreground">
                    {m.api_keys_no_permission()}
                </p>
            </div>
        )
    }

    const openCreate = () => {
        setName('')
        setExpiresAt('')
        setScope('partner')
        setNameError('')
        setPermissions(new Set())
        setPermissionsError('')
        setCreateOpen(true)
    }

    const openEdit = (k: ApiKey) => {
        // Khoá cũ (null) đang toàn quyền → mở ra với mọi ô đã tick, bỏ ô nào là
        // khoá mất quyền đó.
        setEditPermissions(new Set(k.permissions ?? PARTNER_PERMISSIONS))
        setEditError('')
        setEditing(k)
    }

    const submitEdit = async () => {
        if (!editing) return
        if (editPermissions.size === 0) {
            setEditError(m.api_keys_permissions_required())
            return
        }
        try {
            await updatePermsMutation.mutateAsync({
                id: editing.id,
                permissions: orderedPartnerPermissions(editPermissions),
            })
            setEditing(null)
        } catch {
            // error toast handled in the hook
        }
    }

    const submitCreate = async () => {
        // Kiểm hết các ô rồi mới dừng, để mọi lỗi hiện cùng lúc cạnh ô sai.
        let invalid = false
        if (!name.trim()) {
            setNameError(m.api_keys_name_required())
            invalid = true
        } else if ([...name.trim()].length > API_KEY_NAME_MAX) {
            setNameError(
                m.api_keys_name_too_long({ max: String(API_KEY_NAME_MAX) }),
            )
            invalid = true
        }
        const isPartner = scope === 'partner'
        if (isPartner && permissions.size === 0) {
            setPermissionsError(m.api_keys_permissions_required())
            invalid = true
        }
        if (invalid) return
        try {
            const created = await createMutation.mutateAsync({
                name: name.trim(),
                scope: isPartner ? undefined : scope,
                permissions: isPartner
                    ? orderedPartnerPermissions(permissions)
                    : undefined,
                expires_at: expiresAt || undefined,
            })
            if (!created) {
                toast.error(m.api_keys_create_missing_data())
                return
            }
            setCreateOpen(false)
            setRevealed(created)
        } catch {
            // error toast handled in the hook
        }
    }

    const copyKey = async () => {
        if (!revealed) return
        try {
            await navigator.clipboard.writeText(revealed.key)
            toast.success(m.api_keys_copied())
        } catch {
            toast.error(m.api_keys_copy_failed())
        }
    }

    const revoke = (k: ApiKey) => {
        if (!window.confirm(m.api_keys_revoke_confirm({ name: k.name }))) return
        revokeMutation.mutate(k.id)
    }

    const statusBadge = (status: KeyStatus) => {
        if (status === 'active') {
            return <Badge>{m.api_keys_status_active()}</Badge>
        }
        if (status === 'expired') {
            return (
                <Badge variant="secondary">{m.api_keys_status_expired()}</Badge>
            )
        }
        return (
            <Badge variant="destructive">{m.api_keys_status_revoked()}</Badge>
        )
    }

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<KeyRound className="size-5 shrink-0" />}
                title={m.api_keys_title()}
                primaryActions={
                    canWrite ? (
                        <Button size="sm" onClick={openCreate}>
                            <Plus className="size-4" />
                            {m.api_keys_create_btn()}
                        </Button>
                    ) : null
                }
            />

            <div className="overflow-x-auto rounded-lg border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.api_keys_col_name()}</TableHead>
                            <TableHead>{m.api_keys_col_scope()}</TableHead>
                            <TableHead>
                                {m.api_keys_col_permissions()}
                            </TableHead>
                            <TableHead>{m.api_keys_col_expiry()}</TableHead>
                            <TableHead>{m.api_keys_col_status()}</TableHead>
                            {canWrite && (
                                <TableHead className="text-right">
                                    {m.api_keys_col_actions()}
                                </TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={canWrite ? 6 : 5}
                                    className="text-center text-sm text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : keys.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={canWrite ? 6 : 5}
                                    className="text-center text-sm text-muted-foreground"
                                >
                                    {m.api_keys_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            keys.map((k) => {
                                const status = keyStatus(k)
                                return (
                                    <TableRow key={k.id}>
                                        <TableCell className="font-medium">
                                            {k.name}
                                        </TableCell>
                                        <TableCell>
                                            {scopeLabel(k.scope)}
                                        </TableCell>
                                        <TableCell className="max-w-xs text-sm">
                                            {permissionsLabel(k)}
                                        </TableCell>
                                        <TableCell>
                                            {formatExpiry(k.expires_at)}
                                        </TableCell>
                                        <TableCell>
                                            {statusBadge(status)}
                                        </TableCell>
                                        {canWrite && (
                                            <TableCell className="space-x-2 whitespace-nowrap text-right">
                                                {status !== 'revoked' &&
                                                    (k.scope ?? 'partner') ===
                                                        'partner' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() =>
                                                                openEdit(k)
                                                            }
                                                        >
                                                            {m.api_keys_edit_permissions()}
                                                        </Button>
                                                    )}
                                                {status !== 'revoked' && (
                                                    <Button
                                                        variant="destructive"
                                                        size="sm"
                                                        onClick={() =>
                                                            revoke(k)
                                                        }
                                                        disabled={
                                                            revokeMutation.isPending
                                                        }
                                                    >
                                                        {m.api_keys_revoke()}
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{m.api_keys_create_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-2">
                        <div className="grid gap-2">
                            <Label htmlFor="api-key-name">
                                {m.api_keys_name_label()}
                            </Label>
                            <Input
                                id="api-key-name"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value)
                                    if (nameError) setNameError('')
                                }}
                            />
                            {nameError && (
                                <p className="text-sm text-destructive">
                                    {nameError}
                                </p>
                            )}
                        </div>
                        {canWriteStorefront && (
                            <div className="grid gap-2">
                                <Label htmlFor="api-key-scope">
                                    {m.api_keys_scope_label()}
                                </Label>
                                <Select
                                    value={scope}
                                    onValueChange={(v) =>
                                        setScope(v as ApiKeyScope)
                                    }
                                >
                                    <SelectTrigger id="api-key-scope">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="partner">
                                            {m.api_keys_scope_partner()}
                                        </SelectItem>
                                        <SelectItem value="storefront">
                                            {m.api_keys_scope_storefront()}
                                        </SelectItem>
                                        <SelectItem value="shop_auth">
                                            {m.api_keys_scope_shop_auth()}
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {scope === 'partner' && (
                            <PartnerPermissionsField
                                id="api-key-permissions"
                                value={permissions}
                                onChange={(next) => {
                                    setPermissions(next)
                                    if (permissionsError)
                                        setPermissionsError('')
                                }}
                                error={permissionsError}
                            />
                        )}
                        <div className="grid gap-2">
                            <Label htmlFor="api-key-expiry">
                                {m.api_keys_expiry_label()}
                            </Label>
                            <Input
                                id="api-key-expiry"
                                type="date"
                                min={new Date().toLocaleDateString('en-CA')}
                                value={expiresAt}
                                onChange={(e) => setExpiresAt(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground">
                                {m.api_keys_expiry_hint()}
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="flex-row gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="flex-1 sm:flex-none"
                            onClick={() => setCreateOpen(false)}
                            disabled={createMutation.isPending}
                        >
                            {m.api_keys_cancel()}
                        </Button>
                        <Button
                            className="flex-1 sm:flex-none"
                            onClick={submitCreate}
                            disabled={createMutation.isPending}
                        >
                            {m.api_keys_create_submit()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sửa quyền của khoá partner */}
            <Dialog
                open={editing !== null}
                onOpenChange={(open) => {
                    if (!open) setEditing(null)
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {m.api_keys_edit_permissions_title({
                                name: editing?.name ?? '',
                            })}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-2">
                        <PartnerPermissionsField
                            id="api-key-edit-permissions"
                            value={editPermissions}
                            onChange={(next) => {
                                setEditPermissions(next)
                                if (editError) setEditError('')
                            }}
                            error={editError}
                            hint={
                                editing?.permissions == null
                                    ? m.api_keys_edit_permissions_legacy_hint()
                                    : undefined
                            }
                        />
                    </div>
                    <DialogFooter className="flex-row gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="flex-1 sm:flex-none"
                            onClick={() => setEditing(null)}
                            disabled={updatePermsMutation.isPending}
                        >
                            {m.api_keys_cancel()}
                        </Button>
                        <Button
                            className="flex-1 sm:flex-none"
                            onClick={submitEdit}
                            disabled={updatePermsMutation.isPending}
                        >
                            {m.api_keys_save()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* One-time reveal modal */}
            <Dialog
                open={revealed !== null}
                onOpenChange={(open) => {
                    if (!open) setRevealed(null)
                }}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{m.api_keys_reveal_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200">
                            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                            <span>{m.api_keys_reveal_warning()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <code className="min-w-0 flex-1 break-all rounded-md bg-muted px-3 py-2 font-mono text-sm">
                                {revealed?.key}
                            </code>
                            <Button
                                variant="outline"
                                size="icon"
                                className="shrink-0"
                                onClick={copyKey}
                                aria-label={m.api_keys_copy()}
                            >
                                <Copy className="size-4" />
                            </Button>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setRevealed(null)}>
                            {m.api_keys_reveal_done()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
